import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import { CustomInput, CustomToggle } from '../Components/CustomFormElements';
import PageLoader from '../Components/PageLoader';
import UserPhotoInput from '../Components/UserPhotoInput';
import { useAuth } from '../Contexts/AuthContext';
import { UsersApiProvider, useUsersApi } from '../Services/UsersApiProvider';
import { UserApi } from '../Services/UsersApiProviderTypes';

type UserForm = {
  password: string;
  is_active: boolean;
  email: string;
  name: string;
};

const DEFAULT_NEW_USER: UserForm = {
  name: '',
  email: '',
  password: '',
  is_active: true,
};

function UserContent({ canEdit }: { canEdit: boolean }) {
  const { userId } = useParams();
  const isNew = userId === 'new-user';
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { status, refreshStatus } = useAuth();
  const { showConfirmation } = useConfirmation();

  const { getUserById, createUser, updateUser, sendResetPasswordEmail, syncUserPhoto } =
    useUsersApi();

  const [user, setUser] = useState<UserForm | null>(null);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [isVerified, setIsVerified] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [receiveSystemNotifications, setReceiveSystemNotifications] = useState<boolean>(false);
  const [photo, setPhoto] = useState<string>('');
  const [initialPhoto, setInitialPhoto] = useState<string>('');

  useEffect(() => {
    if (isNew) {
      setUser(DEFAULT_NEW_USER);
      setIsActive(true);
      setReceiveSystemNotifications(false);
      setLoading(false);
    } else {
      const fetchUser = async () => {
        try {
          const data: UserApi = await getUserById(userId);
          setUser({ name: data.name, email: data.email, password: '', is_active: data.is_active });
          setIsActive(data.is_active);
          setIsVerified(data.is_verified);
          setReceiveSystemNotifications(data.notify_system);
          setPhoto(data.photo_url ?? '');
          setInitialPhoto(data.photo_url ?? '');
        } catch (error) {
          console.error('Failed to load user', error);
        } finally {
          setLoading(false);
        }
      };
      fetchUser();
    }
  }, [userId, isNew, getUserById]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const base = {
        name: user!.name,
        email: user!.email,
        is_active: isActive,
        is_verified: isVerified,
        notify_system: receiveSystemNotifications,
      };

      let savedUserId = userId;
      if (isNew) {
        const created = await createUser({ ...base, password: user!.password });
        savedUserId = created.id;
      } else {
        const updatePayload = user!.password ? { ...base, password: user!.password } : base;
        await updateUser(userId, updatePayload);
      }

      try {
        const photoChanged = await syncUserPhoto(savedUserId, photo, initialPhoto);
        if (photoChanged && savedUserId === status?.id) refreshStatus?.();
      } catch (error) {
        const reason = error.response?.data?.error || error.response?.statusText || 'error';
        if (isNew) {
          showAlert('warning', `User created, but photo upload failed: ${reason}`);
          navigate(-1);
        } else {
          showAlert('fail', `User saved, but photo update failed: ${reason}`);
        }
        return;
      }

      showAlert('success', isNew ? 'User successfully created!' : 'User successfully updated!');
      navigate(-1);
    } catch (error) {
      console.error('Error saving user', error);
      showAlert(
        'fail',
        `Failed to save user: ${error.response?.data?.error || error.response?.statusText || 'error'}`,
      );
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    showConfirmation(
      `Are you sure you want to send a password reset email to this user?`,
      async () => {
        try {
          await sendResetPasswordEmail(user.email);
          showAlert('success', 'Password reset email was successfully sent!');
        } catch (error) {
          showAlert('fail', 'Failed to send password reset email!');
        }
      },
    );
  };

  if (loading) return <PageLoader />;
  if (!user) return <div className="container">User not found</div>;

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row">
        <div className="col-6 radius-yes p-2 bg-yes">
          <h2 className="h2-title">{user.name}</h2>

          <div className="input-block">
            <UserPhotoInput value={photo} onChange={setPhoto} disabled={!canEdit} />
          </div>

          <div className="input-block">
            <CustomInput
              id="name"
              label="Name"
              value={user.name}
              placeholder="Enter name"
              onChange={(_, val) => setUser((prev) => ({ ...prev, name: val }))}
              disabled={!canEdit}
            />
          </div>

          <div className="input-block">
            <CustomInput
              id="email"
              label="Email"
              value={user.email}
              placeholder="Enter email"
              onChange={(_, val) => setUser((prev) => ({ ...prev, email: val }))}
              disabled={!(isNew && canEdit)}
            />
          </div>

          <div className="input-block">
            <CustomInput
              id="password"
              label="Password"
              value={user.password}
              placeholder={isNew ? 'Enter password' : '••••••••••'}
              onChange={(_, val) => setUser((prev) => ({ ...prev, password: val }))}
              type="password"
              disabled={!(isNew && canEdit)}
            />
          </div>
          {!isNew && status?.permissions?.includes('all:organizations') && (
            <div className="input-block d-flex flex-row justify-between">
              <div className="input-description">
                <div className="input-title">Reset Password</div>
                <div className="small-font">
                  Send an email to allow the user to set a new password
                </div>
              </div>
              <div className="align-wrapper d-flex align-center">
                <Button
                  background="color2"
                  size="medium"
                  onClick={handleResetPassword}
                  disabled={!canEdit}
                >
                  Reset
                </Button>
              </div>
            </div>
          )}

          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">System Notifications</div>
              <div className="small-font">Enable or disable receiving system notifications</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomToggle
                id="system-notifications-toggle"
                label={receiveSystemNotifications ? 'Enabled' : 'Disabled'}
                checked={receiveSystemNotifications}
                onChange={(_, value) => setReceiveSystemNotifications(value)}
                disabled={!canEdit}
              />
            </div>
          </div>

          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Active</div>
              <div className="small-font">Set the User to be active or inactive</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomToggle
                id="active-toggle"
                label={isActive ? 'Active' : 'Inactive'}
                checked={isActive}
                onChange={(_, value) => setIsActive(value)}
                disabled={!canEdit}
              />
            </div>
          </div>

          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Verified</div>
              <div className="small-font">Set the User to be verified or unverified</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomToggle
                id="verified-toggle"
                label={isVerified ? 'Verified' : 'Unverified'}
                checked={isVerified}
                onChange={(_, value) => setIsVerified(value)}
                disabled={!canEdit}
              />
            </div>
          </div>

          <div className="buttons-container d-flex justify-end" style={{ gap: '12px' }}>
            <Button
              background="color2"
              size="medium"
              onClick={handleSave}
              disabled={saving || !canEdit}
            >
              Save
            </Button>
            <Button variant="danger" size="medium" onClick={() => navigate(-1)}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function User({ canEdit }: { canEdit: boolean }) {
  return (
    <ConfirmationProvider>
      <UsersApiProvider>
        <UserContent canEdit={canEdit} />
      </UsersApiProvider>
    </ConfirmationProvider>
  );
}

export default User;
